import { TopicArticles } from "@/components/site/ArticleTeasers";
import { createFileRoute, Link } from "@tanstack/react-router";
import { pageLinks } from "@/lib/locale-alternates";
import { ArrowRight, MessageCircle } from "lucide-react";
import { breadcrumb, faqPage, graph, ORG_ID, service, webPage, WEBSITE_ID, TRAILS } from "@/lib/schema";
import { socialMeta, TR_SERVICES_OG_IMAGE } from "@/lib/seo";
import { whatsappUrl } from "@/lib/whatsapp";
import { PageHero, SectionHeading } from "@/components/site/Blocks";
import { faqsTr } from "@/lib/services-faqs.tr";
import { TrServiceLinks } from "@/components/site/TrServiceLinks";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * Türkçe hizmetler başlığı. Hero, SEO başlığı, sosyal kart başlığı ve Service
 * JSON-LD aynı sabiti kullanır; böylece dördü asla birbirinden ayrışamaz.
 */
export const SERVICES_HEADING_TR = "SAP Danışmanlık Hizmetleri, Stratejik Danışmanlık ve Kaynak Sağlama Çözümleri";

/** Henüz yayımlanmamış TR sayfaları — route tipi hazır olana kadar string olarak tutulur. */
const TR_TECHNOLOGY_CONSULTING_PATH: string = "/tr/hizmetler/teknoloji-danismanligi";
const TR_SHORING_PATH: string = "/tr/nearshore";
const TR_AMS_PATH: string = "/tr/hizmetler/ams";

const title = "SAP Hizmetleri: Danışmanlık, Dönüşüm, Kaynak Sağlama | OXORY";
const description =
  "SAP danışmanlığı ve kaynak sağlama tek adreste: S/4HANA geçişi, SAP AMS, e-Fatura, ABAP ve Fiori, Basis ve nearshore ekipler. 100+ değerlendirilmiş uzman.";

export const Route = createFileRoute("/tr/hizmetler")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      ...socialMeta({
        title,
        description,
        path: "/tr/hizmetler",
        locale: "tr_TR",
        socialTitle: "SAP danışmanlık firması ve SAP kaynak sağlama firması",
        image: TR_SERVICES_OG_IMAGE,
        imageAlt:
          "OXORY danışmanları ve iş ortağı uzmanları Avrupa'da bir kurumsal SAP projesinde çalışıyor",
      }),
    ],
    links: pageLinks("/tr/hizmetler"),
    scripts: [
      graph(
        { "@id": ORG_ID },
        { "@id": WEBSITE_ID },
        webPage({
          path: "/tr/hizmetler",
          name: title,
          description,
          type: "CollectionPage",
          image: TR_SERVICES_OG_IMAGE,
          inLanguage: "tr",
        }),
        breadcrumb(TRAILS["/tr/hizmetler"]),
        service({
          path: "/tr/hizmetler",
          name: SERVICES_HEADING_TR,
          description,
          serviceType: "SAP Consulting, SAP Transformation and SAP Resourcing",
          offers: [
            "SAP Danışmanlık",
            "SAP Kaynak Sağlama (SAP danışmanı kiralama)",
            "SAP Dönüşüm ve S/4HANA Migrasyonu",
            "SAP Proje Desteği",
            "SAP Teknoloji Danışmanlığı",
            "SAP EWM Danışmanlığı",
            "SAP TM Danışmanlığı",
            "Geliştirme Hizmetleri",
            "Uygulama Yönetim Hizmetleri (AMS)",
            "Shoring: Nearshore SAP Delivery",
          ],
        }),
        ...(import.meta.env.SSR ? [faqPage(faqsTr)] : []),
      ),
    ],
  }),
  component: TrServicesPage,
});

type SubItem = {
  id: string;
  name: string;
  lead: string;
  points: string[];
  link?: { to: string; label: string };
};

type Block = {
  id: string;
  eyebrow: string;
  title: string;
  lead: string;
  points?: string[];
  items?: SubItem[];
};

const blocks: Block[] = [
  {
    id: "recruitment",
    eyebrow: "SAP kaynak sağlama",
    title: "SAP kaynak sağlama: freelance ve iş ortağı uzmanlarıyla kadrolama",
    lead: "SAP kaynak sağlama modelimizde her çalışma SAP danışmanlık uzmanlığıyla yönetilir ve değerlendirilmiş freelance ile iş ortağı uzmanlarından oluşan ağımızdan kadrolanır — tek bir SAP uzmanından eksiksiz bir advisory ve delivery ekibine kadar.",
    points: [
      "Freelance ve iş ortağı ağlarından SAP fonksiyonel, teknik ve mimari uzmanları",
      "Daha büyük SAP programları için advisory ekipleri ve delivery merkezi ölçeklendirmeleri",
      "Hiçbir uzman kısa listenize ulaşmadan önce teknik değerlendirme ve referans kontrolü",
      "Şeffaf oranlar, pazar geri bildirimi ve gerçekçi müsaitlik bilgisi",
      "Yalnızca bağımsız freelance çalışanlar değil, OXORY iş ortağı firmalarında çalışan danışmanlara da erişim",
    ],
  },
  {
    id: "transformation",
    eyebrow: "SAP dönüşüm",
    title: "SAP dönüşüm ve S/4HANA migrasyonu",
    lead: "SAP dönüşüm programlarında hazırlık, teknik geçiş ve go-live sonrası istikrar tek bir iş ortağından: brownfield, greenfield ve selective data transition senaryoları için deneyimli SAP danışmanlık ekipleri.",
    points: [
      "SAP S/4HANA dönüşüm değerlendirmesi, readiness kontrolü ve yol haritası",
      "Brownfield, greenfield ve selective data transition arasında senaryo karşılaştırması",
      "Custom code analizi, clean-core yaklaşımı ve uyum çalışmaları",
      "Dönüşüm sırasında test, cutover ve hypercare için ek SAP kaynak sağlama",
    ],
  },
  {
    id: "project-support",
    eyebrow: "SAP Proje desteği",
    title: "Bir SAP projesi ne zaman ek desteğe ihtiyaç duyar?",
    lead: "Bir SAP projesi en çok kritik fazlarında, yani son test aşamasından go-live'a ve istikrarlı işletime kadar ek desteğe ihtiyaç duyar. OXORY bu kapasiteyi tam ihtiyaç duyulduğu anda sağlar.",
    points: [
      "Go-live desteği — SAP cutover ve go-live öncesindeki son etap boyunca ek kapasite",
      "Hypercare — SAP go-live'ının hemen ardındaki haftalarda yakın izleme ve hızlı düzeltmeler",
      "Go-live sonrası AMS — sürekli SAP uygulama yönetimine yapılandırılmış devir",
      "Planlı SAP proje fazları için rollout, migrasyon ve upgrade desteği",
      "Kritik SAP rolleri ve devir dönemleri için geçici (interim) kapsama",
    ],
  },
  {
    id: "technology-consulting",
    eyebrow: "SAP Danışmanlığı",
    title: "SAP Danışmanlığı",
    lead: "Platform genelinde ve modüle özel SAP danışmanlığı tek bir yerde — süreçlerinizin altındaki teknoloji katmanından, onları işleten lojistik modüllerine kadar.",
    items: [
      {
        id: "sap-consulting-general",
        name: "SAP Danışmanlığı — Genel",
        lead: "SAP platform katmanında danışmanlık ve uygulamalı çalışma: iş süreçlerinizin altındaki mimari, entegrasyon, migrasyon ve performans.",
        points: [
          "SAP ortamı, platformu ve sistem mimarisi desteği",
          "SAP ve üçüncü taraf sistemler arasında entegrasyon tasarımı",
          "SAP ve modern ERP ortamları için migrasyon ve dönüşüm planlaması",
          "SAP projeleri için teknik incelemeler, değerlendirmeler ve ikinci görüşler",
        ],
        link: {
          to: TR_TECHNOLOGY_CONSULTING_PATH,
          label: "SAP Teknoloji Danışmanlığı genel bakışının tamamını okuyun",
        },
      },
      {
        id: "sap-ewm",
        name: "SAP EWM Danışmanlığı",
        lead: "SAP Extended Warehouse Management ile depo operasyonlarınızı optimize edin ve verimliliği artırın.",
        points: [
          "Greenfield implementasyon, WM'den migrasyon ve rollout'lar",
          "Mal kabul, sevkiyat, iç hareketler ve sayım süreçlerinin tasarımı",
          "RF ve paketleme süreci optimizasyonu, etiketleme ve otomasyon arayüzleri",
        ],
      },
      {
        id: "sap-tm",
        name: "SAP TM Danışmanlığı",
        lead: "Nakliye süreçlerinizi tek bir SAP Transportation Management sisteminde toplayın ve verimliliği artırın.",
        points: [
          "Nakliye siparişi, planlama ve taşıyıcı seçimi kurulumu",
          "Nakliye maliyeti hesaplama, mutabakat ve forwarding süreçleri",
          "EWM, ERP ve dış taşıyıcı platformlarıyla entegrasyon",
        ],
      },
    ],
  },
  {
    id: "development",
    eyebrow: "Geliştirme hizmetleri",
    title: "Geliştirme Hizmetleri",
    lead: "SAP çevresinde advisory odaklı geliştirme kapasitesi — bireysel uzmanlar tarafından ya da tanımlı kapsamlı yönetilen bir ekip olarak sunulur.",
    points: [
      "SAP ortamları için ABAP, backend ve veritabanı platformu geliştirme",
      "SAP Fiori, modern web uygulamaları ve genişletmeler",
      "SAP ve üçüncü taraf sistemler arasında entegrasyon ve arayüz geliştirme",
      "Dönüşümler öncesinde custom code incelemeleri, clean-core danışmanlığı ve iyileştirme",
    ],
  },
  {
    id: "ams",
    eyebrow: "AMS",
    title: "Uygulama Yönetim Hizmetleri (AMS)",
    lead: "Canlı SAP ve BT ortamları için net kapsam, sorumluluk ve eskalasyon yollarıyla sürekli destek — yalnızca ticket işleme değil, advisory de dahil.",
    points: [
      "SAP uzmanlığı hazır bekleyen olay (incident), problem ve değişiklik yönetimi",
      "SAP modülleri ve bağlı sistemler genelinde fonksiyonel ve teknik destek",
      "Küçük iyileştirmeler ve release desteği",
      "Esnek servis pencereleri ve tanımlı raporlama",
    ],
  },
  {
    id: "shoring",
    eyebrow: "Shoring",
    title: "Shoring: nearshore SAP danışmanlığı ve delivery",
    lead: "Maliyet açısından verimli SAP kapasitesi için Avrupa içi nearshore delivery — advisory kalitesi ve yakınlık korunarak.",
    points: [
      "Belirli proje rolleri için nearshore SAP freelance ve iş ortağı uzmanları",
      "SAP geliştirme ve AMS kapsamı için yönetilen nearshore ekipler",
      "Örtüşen zaman dilimleri ve advisory sorumlunuzla doğrudan iletişim",
      "Tek sözleşme ve tek sorumluluk noktası",
    ],
  },
];

function TrServicesPage() {
  return (
    <>
      <PageHero
        trail={TRAILS["/tr/hizmetler"]}
        eyebrow="Hizmetlerimiz"
        title={SERVICES_HEADING_TR}
        intro="OXORY; SAP danışmanlık, SAP dönüşüm ve SAP kaynak sağlama hizmetlerini tek bir iş ortaklığında birleştirir. OXORY, SAP platformları, lojistik modülleri ve entegrasyon konusunda danışmanlık verir ve her projeyi özenle seçilmiş bağımsız ve iş ortağı uzmanlarıyla ekiplendirir. İster danışmanlık, ister uygulama desteği, ister ikisini birlikte isteyin — hepsini tek ve sorumlu bir iş ortaklığından alırsınız."
      />

      <section className="border-b border-border bg-primary text-primary-foreground">
        <div className="section-shell py-12 md:py-16">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <h2 className="text-2xl font-bold md:text-3xl">İhtiyaç duyduğunuz uzman desteğini bizden talep edin</h2>
              <p className="mt-3 text-primary-foreground/90">
                İhtiyacınızı bize iletin; size en uygun danışmanlık, serbest çalışan ve iş ortağı
                uzmanlarını eşleştiririz — tek bir uzmandan eksiksiz bir uygulama ekibine kadar.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/tr/iletisim"
                className="inline-flex items-center gap-2 rounded-md bg-primary-foreground px-5 py-3 text-sm font-bold text-primary hover:bg-primary-foreground/90"
              >
                Bize ulaşın <ArrowRight className="size-4" />
              </Link>
              <a
                href={whatsappUrl("/tr/hizmetler", { utmContent: "services-cta" })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-primary-foreground/30 px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary-foreground/10"
              >
                <MessageCircle className="size-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="section-shell pt-12">
        <section aria-labelledby="hizmetler-konular" className="max-w-3xl">
          <h2 id="hizmetler-konular" className="text-xl font-bold">
            SAP danışmanlık, S/4HANA, AMS ve e-Fatura tek iş ortağından
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Şirketler OXORY'ye S/4HANA dönüşümlerinde <strong>SAP danışmanlık</strong>, canlı
            kullanım sonrası <strong>SAP AMS</strong> (Uygulama Yönetimi Hizmetleri) ve
            Türkiye'de <strong>e-Fatura</strong>, Almanya'da XRechnung ve Peppol gibi e-fatura
            zorunlulukları için gelir. 10+ iş ortağı firma ve 100+ değerlendirilmiş SAP uzmanıyla
            danışmanlık, geliştirme ve işletmeyi tek sözleşmede birleştiririz.
          </p>
          <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-bold">
            <li><Link to="/tr/hizmetler/ams/sap-ams-nedir" className="text-primary hover:underline">SAP AMS nedir?</Link></li>
            <li><Link to="/tr/cozumler/sap-e-fatura-cozumu" className="text-primary hover:underline">SAP e-fatura ve e-Fatura</Link></li>
            <li><Link to="/tr/sap-projeleri-turkiye" className="text-primary hover:underline">Türkiye'de SAP projeleri</Link></li>
          </ul>
        </section>
      </div>

      <div className="section-shell py-20">
        <nav aria-label="Hizmetlere genel bakış" className="flex flex-wrap gap-2">
          {blocks.map((b) => (
            <a
              key={b.id}
              href={`#${b.id}`}
              className="rounded-md border border-border bg-card px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-foreground"
            >
              {b.eyebrow}
            </a>
          ))}
        </nav>

        <div className="mt-16 space-y-20">
          {blocks.map((b) => (
            <section key={b.id} id={b.id} className="scroll-mt-24">
              <SectionHeading eyebrow={b.eyebrow} title={b.title} lead={b.lead} />
              {b.points ? (
                <ul className="mt-8 grid gap-4 md:grid-cols-2">
                  {b.points.map((p) => (
                    <li
                      key={p}
                      className="rounded-lg border border-border bg-card p-5 text-sm leading-relaxed text-muted-foreground shadow-card"
                    >
                      {p}
                    </li>
                  ))}
                </ul>
              ) : null}
              {b.items ? (
                <div className="mt-8 grid gap-6 md:grid-cols-2">
                  {b.items.map((item) => (
                    <article
                      key={item.id}
                      id={item.id}
                      className="scroll-mt-24 rounded-lg border border-border bg-card p-6 shadow-card"
                    >
                      <h3 className="text-lg font-extrabold">{item.name}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {item.lead}
                      </p>
                      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                        {item.points.map((p) => (
                          <li key={p} className="flex gap-2">
                            <span aria-hidden="true" className="text-primary">
                              —
                            </span>
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                      {item.link ? (
                        <Link
                          to={item.link.to}
                          className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3"
                        >
                          {item.link.label} <ArrowRight className="size-4" />
                        </Link>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : null}
              {b.id === "recruitment" && (
                <Link
                  to="/tr/uzmanlar-icin"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3"
                >
                  Danışman ağımıza katılın <ArrowRight className="size-4" />
                </Link>
              )}
              {b.id === "shoring" && (
                <Link
                  to={TR_SHORING_PATH}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3"
                >
                  Shoring genel bakışının tamamını okuyun <ArrowRight className="size-4" />
                </Link>
              )}
              {(b.id === "ams" || b.id === "project-support") && (
                <Link
                  to={TR_AMS_PATH}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3"
                >
                  AMS genel bakışının tamamını okuyun <ArrowRight className="size-4" />
                </Link>
              )}
            </section>
          ))}
        </div>

        <section className="mt-20">
          <SectionHeading eyebrow="Sık sorulan sorular" title="SAP hizmetleri hakkında" />
          <Accordion type="single" collapsible className="mt-8">
            {faqsTr.map(([question, answer]) => (
              <AccordionItem key={question} value={question}>
                <AccordionTrigger className="text-left text-base font-bold">{question}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <TrServiceLinks className="mt-20" />



        <div className="mt-20 rounded-lg border border-border bg-secondary p-10">
          <h2 className="text-2xl font-bold md:text-3xl">Hangi modelin uyduğundan emin değil misiniz?</h2>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">
            SAP ihtiyacınızı anlatın; bir advisory çalışmasının, tek bir freelance veya iş ortağı
            uzmanının, yönetilen bir ekibin ya da nearshore bir kurulumun daha iyi yol olup
            olmadığını öneririz.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/tr/iletisim"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
            >
              OXORY ile iletişime geçin <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/tr/cozumler/yasal-uyum-araclari"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-secondary"
            >
              Uyumluluk çözümlerini keşfedin: e-fatura ve Peppol <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </div>
    <TopicArticles topic="services-ams" locale="tr" />
    </>
  );
}
